import React, { useEffect, useState } from "react";
import { Form, Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useDispatch, useSelector } from "react-redux";
import { addNewProject, EditRfqProject, fetchRfqProjects, EDIT_RFQ_PROJECT_SUCCESS } from "actions/rfqActions";
import authService from "services/authService";
import PincodeAddressFields from "components/PincodeAddressFields";
import SubProjectManager from "./SubProjectManager";

const addressValues = (location) => ({
  address: location?.addressLine1 || "", area: location?.area || "", city: location?.city || "",
  state: location?.state || "", division: location?.division || "", pincode: String(location?.pinCode || ""),
});
const inputClass = "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-orange-400 focus:ring-orange-100";

const CreateNewProjectForm = ({
  setShowCreateProjectForm,
  isEdit = false,
  data = {},
  onProjectCreated,
  fullHeight = false,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth?.user);
  const entityId = user?.userEntity?.entityId;
  const [project, setProject] = useState(isEdit ? data : null);
  const [step, setStep] = useState(1);
  const [editing, setEditing] = useState(!isEdit);
  const [members, setMembers] = useState([]);
  const [memberError, setMemberError] = useState("");
  const [memberLoading, setMemberLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setMemberLoading(true);
    setMemberError("");
    authService.getEntityMembers(entityId, { signal: controller.signal }).then((result) => {
      if (!controller.signal.aborted) setMembers(result.members);
    }).catch((error) => {
      if (!controller.signal.aborted) setMemberError(error.response?.data?.message || "Unable to load entity members.");
    }).finally(() => { if (!controller.signal.aborted) setMemberLoading(false); });
    return () => controller.abort();
  }, [entityId, attempt]);

  const close = () => {
    if (project?._id) {
      dispatch(fetchRfqProjects(entityId));
      onProjectCreated?.(project);
    }
    setShowCreateProjectForm(false);
  };
  const initialAddress = addressValues(project?.location);
  const membersById = new Map(members.map((member) => [member.userId, member]));
  const validationSchema = Yup.lazy((values) => {
    const unchangedAddress = Boolean(project) && Object.keys(initialAddress).every(
      (key) => String(values[key] || "").trim() === String(initialAddress[key]).trim()
    );
    const requiredAddress = unchangedAddress ? Yup.string().trim() : Yup.string().trim().required("Required");
    return Yup.object({
      name: Yup.string().trim().required("Project name is required").max(120),
      directorUserIds: Yup.array().of(Yup.number()).min(!project || project.directorUserIds?.length ? 1 : 0, "Select at least one director"),
      isActive: Yup.boolean().required(),
      address: requiredAddress, area: requiredAddress, city: requiredAddress, state: requiredAddress,
      pincode: unchangedAddress ? Yup.string() : Yup.string().matches(/^[1-9]\d{5}$/, "Enter a valid 6-digit pincode").required("Required"),
    });
  });

  return (
    <div className={`${fullHeight ? "flex min-h-full flex-col" : "max-h-[calc(100vh-140px)] overflow-y-auto"} bg-[#fcfaf7]`} style={{ fontFamily: "goldman_sans" }}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-orange-100 p-5">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{project?.name || "Create project"}</h2>
          <p className="mt-1 text-xs text-gray-500">{step === 1 ? "Set up the main project, its directors and shared site address." : "Add towers or other sub-projects and their site engineers."}</p>
        </div>
        <nav aria-label="Project setup steps" className="flex gap-2 text-xs">
          <button type="button" onClick={() => setStep(1)} className={`rounded-full px-4 py-2 ${step === 1 ? "bg-[#FD6600] text-white" : "bg-white text-gray-600"}`}>Step - 1: Project</button>
          <button type="button" disabled={!project} onClick={() => setStep(2)} className={`rounded-full px-4 py-2 disabled:opacity-40 ${step === 2 ? "bg-[#FD6600] text-white" : "bg-white text-gray-600"}`}>Step - 2: Sub-projects</button>
        </nav>
      </div>
      {step === 1 ? <Formik
        initialValues={{ name: project?.name || "", directorUserIds: project?.directorUserIds || [], isActive: project?.isActive !== false, ...initialAddress }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setStatus }) => {
          setStatus("");
          try {
            const selectedMembers = new Set(members.map((member) => member.userId));
            if (values.directorUserIds.some((id) => !selectedMembers.has(id))) throw new Error("Remove directors who are no longer members of this entity.");
            const input = {
              entityId, name: values.name.trim(), directorUserIds: values.directorUserIds, isActive: values.isActive,
              location: { addressLine1: values.address, area: values.area, city: values.city, state: values.state, division: values.division, pinCode: values.pincode || undefined },
            };
            const saved = await dispatch(project ? EditRfqProject(input, project._id) : addNewProject(input));
            if (!saved?._id) throw new Error("The project save was not confirmed. Please try again.");
            setProject(saved);
            setEditing(false);
            setStep(2);
          } catch (error) { setStatus(error.response?.data?.message || error.message || "Unable to save project."); }
        }}
      >{({ values, setFieldValue, setFieldTouched, errors, touched, status, isSubmitting }) => <Form className={fullHeight ? "flex flex-1 flex-col" : undefined}>
        <fieldset disabled={!editing || isSubmitting} className={`${fullHeight ? "flex-1 " : ""}space-y-5 p-5 disabled:opacity-90`}>
          <section className="grid gap-5 rounded-xl border border-gray-200 bg-white p-5 md:grid-cols-2">
            <div>
              <label htmlFor="project-name" className="mb-2 block text-sm font-semibold">Project name *</label>
              <Field id="project-name" name="name" className={inputClass} />
              <ErrorMessage name="name" component="p" className="mt-1 text-xs text-red-600" />
              <p className="mt-2 text-xs text-gray-500">Vendors see the main project name on the RFQ.</p>
            </div>
            <div>
              <p id="project-directors-label" className="mb-2 text-sm font-semibold">Directors{!project || project.directorUserIds?.length ? " *" : ""}</p>
              {memberLoading ? <p role="status" className="text-sm text-gray-500">Loading entity members...</p> : memberError ? <div role="alert" className="text-sm text-red-600">{memberError} <button type="button" onClick={() => setAttempt((value) => value + 1)} className="underline">Retry</button></div> : <Select
                id="project-directors"
                labelId="project-directors-label"
                name="directorUserIds"
                multiple
                displayEmpty
                fullWidth
                size="small"
                value={values.directorUserIds}
                disabled={!editing || isSubmitting}
                onChange={(event) => setFieldValue("directorUserIds", event.target.value)}
                onBlur={() => setFieldTouched("directorUserIds", true)}
                error={Boolean(touched.directorUserIds && errors.directorUserIds)}
                inputProps={{ "aria-describedby": "project-directors-error" }}
                renderValue={(selected) => selected.length ? selected.map((id) => {
                  const member = membersById.get(id);
                  return member ? member.name || `Member ${id}` : `Former member ${id}`;
                }).join(", ") : <span className="text-gray-400">Select directors</span>}
                sx={{ borderRadius: "8px", fontFamily: "inherit", fontSize: 14, "& .MuiSelect-select": { py: 1.2 }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FD6600" } }}
                MenuProps={{ PaperProps: { sx: { mt: 0.5, borderRadius: "12px", maxHeight: 300, maxWidth: "calc(100vw - 32px)" } } }}
              >
                {members.map((member) => <MenuItem key={member.userId} value={member.userId} sx={{ fontFamily: "inherit", fontSize: 14, whiteSpace: "normal" }}>
                  <Checkbox checked={values.directorUserIds.includes(member.userId)} size="small" tabIndex={-1} inputProps={{ "aria-hidden": true }} sx={{ p: 0, mr: 1.5, "&.Mui-checked": { color: "#FD6600" } }} />
                  <span>{member.name || `Member ${member.userId}`}<span className="ml-2 text-xs text-gray-500">{member.designation}</span></span>
                </MenuItem>)}
                {values.directorUserIds.filter((id) => !membersById.has(id)).map((id) => <MenuItem key={id} value={id} sx={{ fontFamily: "inherit", fontSize: 14, color: "#B45309" }}>
                  <Checkbox checked size="small" tabIndex={-1} inputProps={{ "aria-hidden": true }} sx={{ p: 0, mr: 1.5, "&.Mui-checked": { color: "#FD6600" } }} />
                  Former member {id}
                </MenuItem>)}
                {!members.length && <MenuItem disabled>No entity members found.</MenuItem>}
              </Select>}
              <ErrorMessage id="project-directors-error" name="directorUserIds" component="p" className="mt-1 text-xs text-red-600" />
              {project?.projectDirector && !project.directorUserIds?.length && <p className="mt-2 text-xs text-amber-700">Legacy director: {project.projectDirector}. Select the matching member to map this project.</p>}
            </div>
            <div className="md:col-span-2">
              <label htmlFor="project-status" className="mb-2 block text-sm font-semibold">Project status</label>
              <Field as="select" id="project-status" name="isActive" value={String(values.isActive)} onChange={(event) => setFieldValue("isActive", event.target.value === "true")} className={`${inputClass} md:max-w-xs`}>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Field>
              <p className="mt-2 text-xs text-gray-500">Inactive main projects are hidden from new RFQ selection. Existing RFQs remain unchanged.</p>
            </div>
          </section>
          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="mb-3 font-semibold">Site address *</h3>
            <PincodeAddressFields preserveSavedAddress={Boolean(project)} />
            {project && !project.location?.pinCode && <p className="mt-3 text-xs text-amber-700">The saved legacy address can stay unchanged. Complete the pincode-based address if you change it.</p>}
          </section>
          <p className="rounded-lg border border-orange-100 bg-orange-50 px-4 py-3 text-xs text-gray-600">Sub-projects share this address. Site engineers are added against each sub-project in the next step.</p>
        </fieldset>
        {status && <p role="alert" className="px-5 pb-3 text-sm text-red-600">{status}</p>}
        <div className="sticky bottom-0 flex flex-wrap justify-between gap-3 border-t border-gray-200 bg-white px-5 py-4">
          <button type="button" disabled={isSubmitting} onClick={close} className="rounded-lg border border-gray-200 px-4 py-2 text-sm">{project ? "Done" : "Cancel"}</button>
          <div className="flex gap-2">
            {!editing && <button type="button" onClick={() => setEditing(true)} className="rounded-lg border border-orange-200 px-4 py-2 text-sm text-orange-700">Edit project</button>}
            {editing ? <button type="submit" disabled={isSubmitting || memberLoading || Boolean(memberError)} className="rounded-lg bg-[#FD6600] px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">{isSubmitting ? "Saving..." : "Save & add sub-projects"}</button> : <button type="button" onClick={() => setStep(2)} className="rounded-lg bg-[#FD6600] px-5 py-2 text-sm font-semibold text-white">Manage sub-projects</button>}
          </div>
        </div>
      </Form>}</Formik> : <SubProjectManager entityId={entityId} project={project} onDone={close} onCountChange={(subProjectCount) => {
        const updated = { ...project, subProjectCount };
        setProject(updated);
        dispatch({ type: EDIT_RFQ_PROJECT_SUCCESS, payload: updated });
      }} />}
    </div>
  );
};

export default CreateNewProjectForm;
